import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { CreateInDialogButton } from '@react-admin/ra-form-layout'
import { useState } from 'react'
import {
  CREATE,
  useDataProvider,
  useInfiniteGetList,
  useNotify,
  useRedirect,
  useTranslate,
  useUpdate,
} from 'react-admin'

import { getGroupPath } from './routes'
import { SYSTEM_PERMISSION_CREATE } from '../../config/permissions'
import { useResourcePermissions } from '../../domain/permissions'
import { useSmallScreen } from '../../utils/theme'
import UserGroupFormLayout, { transformValues as transformUserGroup } from '../userGroups/form'
import VehicleGroupFormLayout, { transformValues as transformVehicleGroup } from '../vehicleGroups/form'

const linkSx = { p: 0, minWidth: 0, textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }

const FailedMembershipActions = ({
  currentGroupId,
  currentGroupName,
  memberResource,
  members,
  onAssigned,
  onRemoved,
  canUpdate,
  deleting = false,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const isSmallScreen = useSmallScreen()
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const isUsers = memberResource === 'users'
  const groupResource = isUsers ? 'user-groups' : 'vehicle-groups'
  const memberSource = isUsers ? 'user_ids' : 'vehicle_ids'
  const accessSource = isUsers ? 'vehicle_group_ids' : 'user_group_ids'
  const GroupFormLayout = isUsers ? UserGroupFormLayout : VehicleGroupFormLayout
  const transform = isUsers ? transformUserGroup : transformVehicleGroup
  const [canCreate] = useResourcePermissions(groupResource, SYSTEM_PERMISSION_CREATE)
  const dataProvider = useDataProvider()
  const [updateGroup] = useUpdate(groupResource, {}, { mutationMode: 'pessimistic', returnPromise: true })
  const [dialog, setDialog] = useState(null)
  const [bulkTargetId, setBulkTargetId] = useState('')
  const [individualTargets, setIndividualTargets] = useState({})
  const [saving, setSaving] = useState(false)
  const [removeAfterAssign, setRemoveAfterAssign] = useState(false)
  const [actionError, setActionError] = useState(null)
  const { data, isPending, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteGetList(
    groupResource,
    { pagination: { page: 1, perPage: 50 }, sort: { field: 'name', order: 'ASC' }, filter: {} },
    { enabled: dialog !== null },
  )
  const groups = (data?.pages.flatMap((page) => page.data) || []).filter(
    (group) => String(group.id) !== String(currentGroupId),
  )
  const defaultValues = { [memberSource]: members.map(({ id }) => id), [accessSource]: [] }
  const translationOptions = { smart_count: members.length }
  const createLabel = translate(`mymove.accessGroups.createForMembers.${memberResource}`, translationOptions)
  const actionSx = {
    ...linkSx,
    width: { xs: '100%', md: 'auto' },
    justifyContent: 'flex-start',
    textAlign: 'left',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    '& .MuiButton-startIcon': {
      ml: { xs: 0, sm: '-2px' },
      mr: { xs: 0.5, sm: 1 },
    },
  }
  const finishAssignment = async (memberIds) => {
    if (removeAfterAssign && canUpdate) {
      try {
        const { data: group } = await dataProvider.getOne(groupResource, { id: currentGroupId })
        const assignedIds = new Set(memberIds.map(String))
        await updateGroup(groupResource, {
          id: currentGroupId,
          previousData: group,
          data: {
            name: group.name,
            description: group.description,
            [memberSource]: group[memberSource].filter((id) => !assignedIds.has(String(id))),
          },
        })
      } catch (error) {
        onAssigned(memberIds)
        notify(`mymove.accessGroups.removalFailed.${memberResource}`, {
          type: 'error',
          messageArgs: { error: error.message, name: currentGroupName },
        })
        return
      }
      await onRemoved()
      return
    }
    onAssigned(memberIds)
  }
  const createMutationOptions = {
    onSuccess: async (record) => {
      notify('ra.notification.created', { type: 'info', messageArgs: { smart_count: 1 } })
      await finishAssignment(members.map(({ id }) => id))
      redirect(getGroupPath(groupResource, record.id))
    },
    onError: (error) => setActionError(error.message),
  }
  const closeDialog = () => {
    setDialog(null)
    setBulkTargetId('')
    setIndividualTargets({})
    setRemoveAfterAssign(false)
    setActionError(null)
  }

  const removeCheckbox = (marginLeft = 0, checkboxMarginLeft = marginLeft) => (
    <FormControlLabel
      sx={{
        ml: marginLeft,
        mr: 0,
        alignItems: 'center',
        '& .MuiCheckbox-root': { pl: 0, '& .MuiSvgIcon-root': { transform: 'translateX(-3px)' } },
      }}
      control={
        <Checkbox
          checked={removeAfterAssign}
          disabled={!canUpdate || saving}
          onChange={(event) => setRemoveAfterAssign(event.target.checked)}
          sx={{
            ml: checkboxMarginLeft,
          }}
        />
      }
      label={translate(`mymove.accessGroups.removeAfterAssign.${memberResource}`, { name: currentGroupName })}
    />
  )

  const groupSelector = (value, onChange) => (
    <Autocomplete
      options={groups}
      loading={isPending}
      value={groups.find((group) => String(group.id) === String(value)) || null}
      getOptionLabel={(group) => group.name}
      isOptionEqualToValue={(group, selected) => String(group.id) === String(selected.id)}
      onChange={(_, group) => onChange(group?.id || '')}
      noOptionsText={translate('ra.navigation.no_results')}
      renderInput={(params) => (
        <TextField {...params} size="small" label={translate('mymove.accessGroups.targetGroup')} />
      )}
      sx={{ flex: 1, minWidth: 0 }}
    />
  )

  const assign = async (targets) => {
    const byGroup = new Map()
    for (const [memberId, groupId] of Object.entries(targets)) {
      const ids = byGroup.get(groupId) || []
      ids.push(memberId)
      byGroup.set(groupId, ids)
    }

    setSaving(true)
    setActionError(null)
    const assignedIds = []
    try {
      for (const [groupId, memberIds] of byGroup) {
        const { data: group } = await dataProvider.getOne(groupResource, { id: groupId })
        await updateGroup(groupResource, {
          id: groupId,
          previousData: group,
          data: {
            name: group.name,
            description: group.description,
            [memberSource]: [...new Set([...(group[memberSource] || []), ...memberIds])],
          },
        })
        assignedIds.push(...memberIds)
      }
      await finishAssignment(assignedIds)
      closeDialog()
      notify('mymove.accessGroups.assignmentSaved', { type: 'info' })
    } catch (error) {
      if (assignedIds.length) onAssigned(assignedIds)
      setActionError(error.message)
    } finally {
      setSaving(false)
    }
  }

  if (!canCreate && !canUpdate) return null

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={{ xs: 1.5, md: 2 }}
      useFlexGap
      flexWrap="wrap"
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ mt: 2, minWidth: 0 }}
    >
      {canCreate && (
        <CreateInDialogButton
          resource={groupResource}
          label={createLabel}
          icon={
            isXSmall ? (
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', typography: 'body2' }}>
                <OpenInNewIcon sx={{ fontSize: 18 }} />
                <Box
                  component="span"
                  sx={{ minWidth: 0, overflowWrap: 'anywhere', textTransform: 'uppercase', pl: 0.5 }}
                >
                  {createLabel}
                </Box>
              </Box>
            ) : undefined
          }
          ButtonProps={{ type: 'button', variant: 'text', sx: actionSx, onClick: () => setRemoveAfterAssign(false) }}
          fullScreen={isSmallScreen}
          mutationMode="pessimistic"
          transform={transform}
          mutationOptions={createMutationOptions}
        >
          <GroupFormLayout type={CREATE} defaultValues={defaultValues}>
            {!deleting && <Box sx={{ mt: 3, pl: 1.75 }}>{removeCheckbox()}</Box>}
          </GroupFormLayout>
        </CreateInDialogButton>
      )}
      {canUpdate && (
        <>
          <Button
            type="button"
            variant="text"
            size="small"
            startIcon={<OpenInNewIcon />}
            sx={actionSx}
            onClick={() => {
              setRemoveAfterAssign(false)
              setDialog('bulk')
            }}
          >
            {translate(`mymove.accessGroups.assignAll.${memberResource}`, translationOptions)}
          </Button>
          {members.length > 1 && (
            <Button
              type="button"
              variant="text"
              size="small"
              startIcon={<OpenInNewIcon />}
              sx={actionSx}
              onClick={() => {
                setRemoveAfterAssign(false)
                setDialog('individual')
              }}
            >
              {translate(`mymove.accessGroups.assignIndividually.${memberResource}`)}
            </Button>
          )}
        </>
      )}
      <Dialog
        open={dialog !== null}
        onClose={saving ? undefined : closeDialog}
        fullWidth
        fullScreen={isSmallScreen && dialog === 'individual'}
        maxWidth="sm"
      >
        <DialogTitle sx={{ ml: dialog === 'bulk' ? 2 : isSmallScreen && dialog === 'individual' ? 1.75 : 0 }}>
          {translate(
            `mymove.accessGroups.${dialog === 'individual' ? 'assignIndividually' : 'assignAll'}.${memberResource}`,
            translationOptions,
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {dialog === 'bulk' && groupSelector(bulkTargetId, setBulkTargetId)}
            {dialog === 'individual' && (
              <Stack spacing={2}>
                {members.map(({ id, name }) => (
                  <Stack key={id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        alignSelf: 'stretch',
                        display: 'flex',
                        alignItems: 'center',
                        pl: { xs: 1.75, sm: 0 },
                        pt: { xs: 0, sm: 2 },
                      }}
                    >
                      {name}
                    </Typography>
                    <Box sx={{ flex: 2, minWidth: 0 }}>
                      {groupSelector(individualTargets[id], (groupId) =>
                        setIndividualTargets((targets) => ({ ...targets, [id]: groupId })),
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
            {hasNextPage && (
              <Button
                type="button"
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
                sx={{ alignSelf: 'flex-start' }}
              >
                {translate('mymove.accessGroups.moreGroups')}
              </Button>
            )}
            {!deleting &&
              removeCheckbox(dialog === 'bulk' ? 2 : 0, isSmallScreen && dialog === 'individual' ? 1.75 : undefined)}
            {actionError && <Typography color="error">{actionError}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: isSmallScreen && dialog === 'individual' ? 'calc(16px + env(safe-area-inset-bottom))' : 2,
            borderTop: isSmallScreen && dialog === 'individual' ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <Button type="button" disabled={saving} onClick={closeDialog}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={saving || (dialog === 'bulk' ? !bulkTargetId : members.some(({ id }) => !individualTargets[id]))}
            onClick={() =>
              assign(
                Object.fromEntries(
                  members.map(({ id }) => [id, dialog === 'bulk' ? bulkTargetId : individualTargets[id]]),
                ),
              )
            }
          >
            {translate('mymove.accessGroups.confirmAssignment')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default FailedMembershipActions
